<script setup lang="ts">
import i18n from '@/lang/i18n';
import RegisterModal from "@/components/Common/RegisterModal.vue";
import { Ref, ref, computed, reactive, nextTick, watch } from 'vue';
import DeliveryPlan from "@/types/DeliveryPlan";
import UnexpectedEventRegisterContent from "@/components/DailyReportUpdateModalParts/UnexpectedEventRegisterContent.vue"
import { ObjectToFormData, formatDateTime, firstFile } from '@/services/functions';
import Api from "@/services/api/ApiServiceFabrick";
import { scrollToErrorComponent } from '@/services/validators';
import ModalFlashMessage from "@/components/Common/ModalFlashMessage.vue";
import getUnexpectedEvent from "@/types/responses/getUnexpectedEvent";
import UnexpectedEventFileType from '@/types/UnexpectedEventFile'

const emits = defineEmits(['update:modelValue', 'cancel', 'openModal'])

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    title: string
    deliveryPlan: DeliveryPlan
    unexpectedEvent: getUnexpectedEvent
    isUnexpectedEventLoading: boolean
    getUnexpectedEventAndLongTimeDrivingExtensions?: () => void
    createAuthority: boolean
    updateAuthority: boolean
    showFlash: (message: string, show: boolean, type: string, fadeout: boolean) => void
  }>(),
  {
    isUnexpectedEventLoading: false,
    createAuthority: true,
    updateAuthority: true,
  }
)

type errorTYpe = {
  'start_time': string[],
  'end_time': string[],
  'address': string[],
  'note': string[],
  'files.0': string[]
  'files.1': string[]
  'files.2': string[]
  'files.3': string[]
  'files.4': string[]
}

type flashMessagesType = {
  message: string,
  show: boolean,
  type: string,
  cancel: boolean
}

const errors = ref({} as errorTYpe)
const isLoading: Ref<boolean> = ref(false)
const isFileLoading: Ref<boolean> = ref(false)
const registeredFiles = ref([] as UnexpectedEventFileType[])
const deleteFiles = ref([] as { id: number, file_path: string }[])
const id: Ref<number | string> = ref('')
const isDownloadings: Ref<boolean[]> = ref([false])

const flashMessage = reactive({
  message: "",
  show: false,
  type: "info",
  cancel: false
}) as flashMessagesType

const modelValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emits('update:modelValue', value);
  },
})

const isDisplayConservationButton = computed(() => {
  if (props.unexpectedEvent.id) return props.updateAuthority
  return props.createAuthority
})

const form = reactive({
  delivery_plan_id: props.deliveryPlan.delivery_plan_id ?? '',
  start_time: props.deliveryPlan.leaving_warehouse_date_time ?? new Date(),
  end_time: props.deliveryPlan.returning_warehouse_date_time ?? new Date(),
  address: '',
  note: '',
  files: [
    []
  ]
})

function createOrUpdate() {
  isLoading.value = true
  defaultErrors()
  const requestParams = makeRequestParams()
  Api().createOrUpdateUnexpectedEvent(requestParams, id.value).then(() => {
    props.showFlash(i18n.global.t('pages.common.messages.success'), true, "info", true)
    closeModal(true)
  }).catch((error) => {
      isLoading.value = false
      const res = error.response;
      switch (res.status) {
        case 403:
          location.reload();
          break;
        case 422:
          errors.value = res.data.errors;
          scrollToErrorComponent();
          break;
      }
  })
}

function makeRequestParams() {
  let requestParams = JSON.parse(JSON.stringify(form));
  requestParams.start_time = formatDateTime(new Date(requestParams.start_time))
  requestParams.end_time = formatDateTime(new Date(requestParams.end_time))
  requestParams.delete_files = deleteFiles.value

  let formData = new FormData()
  form.files.forEach((file: File | File[] | null, index: number) => {
    const selected = firstFile(file)
    if (!selected) return
    formData.append(`files[${index}]`, selected)
  })
  ObjectToFormData(requestParams, formData, '')
  return formData
}

function defaultErrors() {
  errors.value.start_time = []
  errors.value.end_time = []
  errors.value.address = []
  errors.value.note = []
  for (let i = 0; i < 5; i++) {
    errors.value[`files.${i}`] = []
  }
}

function changeFlashMessages(resultStatus = "", message = "") {
  flashMessage.type = resultStatus
  flashMessage.message = message
  flashMessage.show = true
  scrollTop()
}

function closeModal(isCreatedOrUpdated: boolean) {
  if (isCreatedOrUpdated && props.getUnexpectedEventAndLongTimeDrivingExtensions) props.getUnexpectedEventAndLongTimeDrivingExtensions()
  modelValue.value = false
  defaultErrors()
}

function scrollTop() {
  nextTick(() => {
    const scrollPoint = document.getElementById('flash-message-component')
    scrollPoint?.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" })
  })
}

function makeForm() {
  id.value = props.unexpectedEvent.id
  form.delivery_plan_id = props.unexpectedEvent.delivery_plan_id
  form.start_time = props.unexpectedEvent.start_time
  form.end_time = props.unexpectedEvent.end_time
  form.address = props.unexpectedEvent.address
  form.note = props.unexpectedEvent.note
  if (props.unexpectedEvent.files?.length) addFiles()
}

function addFiles() {
  isFileLoading.value = true
  for (let i = 0; i < props.unexpectedEvent.files.length; i++) {
    const file = props.unexpectedEvent.files[i];
    Api().getFileUrl("delivery_plan_unexpected_events", file.id).then((data) => {
      registeredFiles.value.push({
        id: props.unexpectedEvent.files[i].id,
        fileBlob: data,
        originalFileName: file.original_file_name,
      })
      registeredFiles.value = sortFiles(registeredFiles.value)
      if (i === props.unexpectedEvent.files.length - 1) isFileLoading.value = false
    }).catch((error) => {
      if (i === props.unexpectedEvent.files.length - 1) isFileLoading.value = false
      const res = error.response;
      switch (res.status) {
        case 404:
          deleteFiles.value.push({
            id: props.unexpectedEvent.files[i].id,
            file_path: props.unexpectedEvent.files[i].file_path
          })
          break
        case 403:
          location.reload();
          break;
      }
    })
  }
}

function sortFiles(registeredFiles: UnexpectedEventFileType[]) {
  return registeredFiles.sort(function(a, b) {
    return (a.id < b.id) ? -1 : 1;
  });
}

function deleteRegisteredFile(index: number, file: UnexpectedEventFileType) {
  deleteFiles.value.push({
    id: file.id,
    file_path: file.fileBlob
  })
  registeredFiles.value.splice(index, 1)
}

function downloadFile(fileId: number, index: number) {
  isDownloadings.value[index] = true
  const requestFile = makeRequest(fileId)
  Api().unexpectedEventFileDownload(requestFile.file_path).then((result: boolean) => {
    isDownloadings.value[index] = false
  }).catch((error) => {
    isDownloadings.value[index] = false
    const res = error.response;
    switch (res.status) {
      case 404:
        changeFlashMessages("error", i18n.global.t('pages.unexpectedEventAndAlertStatuses.fileDownload.notExistsFilePath'))
        break;
      case 403:
        location.reload();
        break;
    }
  })
}

function makeRequest(fileId: number) {
  return props.unexpectedEvent.files.find((unexpectedFile: any) => {
    return fileId == unexpectedFile.id
  })
}

watch(props, () => {
  if (props.unexpectedEvent) makeForm()
})
</script>
<template>
  <RegisterModal
    v-model="modelValue"
    modalMaxWidth="45%"
    :title="props.title"
  >
    <template v-slot:flash-message>
      <ModalFlashMessage
        id="flash-message-component"
        :message = "flashMessage.message"
        :show = "flashMessage.show"
        :type = "flashMessage.type"
        :cancel = "flashMessage.cancel"
        @setShow = "(show) => {flashMessage.show = show}"
      />
    </template>
    <template v-slot:content>
      <template v-if="props.isUnexpectedEventLoading || isFileLoading">
        <div class="list-loading h-100">
          <label>
            {{ i18n.global.t('pages.common.list.reading') }}
          </label>
        </div>
      </template>
      <UnexpectedEventRegisterContent
        v-model="form"
        class="mt-5"
        :registeredFiles="registeredFiles"
        :deleteRegisteredFile="deleteRegisteredFile"
        :downloadFile="downloadFile"
        :isDownloadings=isDownloadings
        :errors="errors"
      />
    </template>
    <template v-slot:buttons>
      <v-btn
        class="ma-2 reset-btn"
        @click="closeModal(false)"
      >
        {{ i18n.global.t('common.cancelBtn') }}
      </v-btn>
      <v-btn
        v-if="isDisplayConservationButton"
        class="ma-2 exec-btn"
        :loading="isLoading"
        @click="createOrUpdate"
      >
        {{ i18n.global.t('pages.common.button.conservation') }}
      </v-btn>
    </template>
  </RegisterModal>
</template>
